import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import type * as React from "react";

import { cn } from "../lib/cn";
import { AndoBrainLoader } from "./blob/AndoBrainLoader";

/**
 * LogoutOverlay — full-screen branded takeover shown WHILE the logout tears
 * the session down (Aug 27, 2026).
 *
 * Before this, pressing "Esci" closed the menu and the app sat frozen until
 * the BE round-trip finished and the hard redirect fired — no feedback at
 * all ("il solito loader… che è lento"). This paints the pencil-line brand
 * loader on a solid cream sheet the instant the user commits, so the wait
 * reads as Ando working, not the app hanging.
 *
 * Design notes (pencil language):
 *   - SOLID ``bg-cream`` (not translucent): the surface below is about to be
 *     unmounted by a location change — blurring it just leaks a dying UI.
 *   - One accent only; the loader's own stroke carries the brand.
 *   - 240ms fade + slight rise, disabled under ``prefers-reduced-motion``.
 *   - ``role="status"`` + ``aria-live`` so screen readers announce the label.
 */
export interface LogoutOverlayProps {
  /** Show/hide — kept mounted via ``AnimatePresence`` for the exit fade. */
  readonly open: boolean;
  /** Localised main line, e.g. "Disconnessione in corso…". */
  readonly label: string;
  /** Optional localised subline, e.g. "A presto". */
  readonly subline?: string;
  readonly className?: string;
}

export const LogoutOverlay: React.FC<LogoutOverlayProps> = ({
  open,
  label,
  subline,
  className,
}) => {
  const reduced = useReducedMotion();
  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="logout-overlay"
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduced ? undefined : { opacity: 0 }}
          transition={{ duration: 0.24, ease: "easeOut" }}
          className={cn(
            "fixed inset-0 z-[120] flex flex-col items-center justify-center gap-5 bg-cream",
            className,
          )}
          role="status"
          aria-live="polite"
        >
          <motion.div
            initial={reduced ? false : { y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.32, ease: "easeOut", delay: 0.06 }}
            className="flex flex-col items-center gap-4"
          >
            <AndoBrainLoader size="md" />
            <div className="flex flex-col items-center gap-1">
              <p className="font-medium text-[15px] text-ink-1">{label}</p>
              {subline ? <p className="text-[13px] text-ink-3">{subline}</p> : null}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};
LogoutOverlay.displayName = "LogoutOverlay";
